<script setup lang="ts">
import AttendanceSessionPickerCard from "~/components/Attendance/review/AttendanceSessionPickerCard.vue";
import AttendanceBlockedCard from "~/components/Attendance/review/AttendanceBlockedCard.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";

/**
 * What the attendance run is waiting on, shown *inside* the assistant's own bubble: the session to
 * confirm, or the proposal to open. The picker stays after the choice, as the record of it. The
 * recap of a confirmed run is not here — the design gives it a card of its own, so the panel
 * renders it as its own message.
 */
withDefaults(defineProps<{ busy?: boolean }>(), { busy: false });

const store = useAttendanceReviewStore();

/**
 * The session detection settled on its own, named so the admin can check it. Out when the picker is
 * on screen: the card they ticked there already says which session the attendance is built on.
 */
const matchedSession = computed(() => (store.hasSessionCandidates ? null : store.matchedSession));
</script>

<template>
  <div class="flex flex-col gap-4">
    <AttendanceBlockedCard v-if="store.blockedError" :error="store.blockedError" />
    <AttendanceSessionPickerCard v-if="store.hasSessionCandidates" :disabled="busy" />

    <!--
      The session the sheet was matched to. Detection settles on one without asking whenever it is
      certain, so this is the whole check on it: named here, and acted on by the button below.
    -->
    <div v-if="matchedSession && store.canOpenReview && !busy" class="flex flex-col gap-2.5">
      <span class="body_regular text-primary-color">Ces présences seront déclarées sur la session suivante :</span>

      <div class="flex flex-col gap-0.5 rounded-lg border border-stroke-color bg-light-color px-4 py-3">
        <div class="flex min-w-0 flex-wrap items-center gap-1.5 text-primary-color">
          <SvgIcon name="graduation_cap_on_v2" class="h-4 w-4 shrink-0" />
          <span class="body_demi">{{ matchedSession.title }}</span>
        </div>

        <div v-if="matchedSession.dates" class="flex items-center gap-1.5 text-secondary-color">
          <SvgIcon name="date" class="h-4 w-4 shrink-0" />
          <span class="meta_regular">{{ matchedSession.dates }}</span>
        </div>
      </div>

      <span class="meta_regular text-secondary-color">
        Si ce n'est pas la bonne session, indiquez-la dans la conversation.
      </span>
    </div>

    <!--
      The assistant's answer ends on "Ouvrir les présences pré-remplies" — this is that. It is
      rendered here rather than by the stream because the answer carries no action part.
    -->
    <LoadButton
      v-if="store.canOpenReview && !busy"
      v-model="store.isAnalyzing"
      class="button__default w-full"
      text-confirm="Ouvrir les présences pré-remplies"
      name-icon="arrow_right"
      reverse-icon
      force-waiting
      @click="store.openPendingReview()"
    />
  </div>
</template>
